import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import type { ImpactResult } from '../../data/impactTypes';
import { formatCompact } from '../../data/impactTypes';
import { InfoTooltip, type MetricTooltip } from '../OverviewPanel';

import { projectionsCardStyles as styles } from './ProjectionsCard.styles';

interface ProjectionsCardProps {
  /** `null` while loading (first paint) or after a failed /impact/result call. */
  result: ImpactResult | null;
  /** Set only when `result` is null because the call failed (vs. still loading). */
  resultError: string | null;
  amountDollars: number;
}

const PER_CHILD_TOOLTIP: MetricTooltip = {
  description:
    'Assumes a minimum $500 seed contribution across children on day 1. Not all children in the selected region may be accounted for.',
};

/**
 * "Nice" axis step (1/2/2.5/5 × 10^n) for ~`targetTicks` ticks across
 * `dataMax`, so labels land on round numbers instead of arbitrary fractions.
 */
function niceStep(dataMax: number, targetTicks = 4): number {
  if (dataMax <= 0) return 1;
  const rawStep = dataMax / targetTicks;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalized = rawStep / magnitude;
  const niceNormalized = [1, 2, 2.5, 5, 10].find((n) => n >= normalized) ?? 10;
  return niceNormalized * magnitude;
}

/** Y-axis ticks at a nice round step, reaching a bit past the data's max. */
function niceTicks(dataMax: number, targetTicks = 4): number[] {
  const step = niceStep(dataMax, targetTicks);
  // At least 5% headroom above the actual max so the top point/marker never
  // sits flush with the topmost gridline.
  const count = Math.max(1, Math.ceil((dataMax * 1.05) / step));
  return Array.from({ length: count + 1 }, (_, i) => i * step);
}

/** Format a y-axis dollar tick — precision follows the tick step, not the
 *  max, so ticks never collapse into duplicate-looking labels. */
function fmtTick(value: number, step: number): string {
  if (step >= 1000) return `$${Math.round(value / 1000)}K`;
  if (step >= 1) return `$${Math.round(value)}`;
  return `$${value.toFixed(2)}`;
}

/** Smooth a polyline into a Catmull-Rom → cubic-bezier path (trend-line look). */
function smoothPath(pts: Array<{ x: number; y: number }>): string {
  if (pts.length < 2) return '';
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

/** Closed ribbon between two smoothed curves sharing the same x positions. */
function smoothBandPath(
  lowerPts: Array<{ x: number; y: number }>,
  upperPts: Array<{ x: number; y: number }>,
): string {
  if (lowerPts.length < 2) return '';
  const lower = smoothPath(lowerPts);
  const upperReversed = smoothPath([...upperPts].reverse()).replace(/^M/, 'L');
  return `${lower} ${upperReversed} Z`;
}

/** Compact inline SVG: amount line + the API's lower/upper neighbor band. */
function ProjectionChart({ result }: { result: ImpactResult }) {
  const theme = useTheme();
  const { projection } = result;
  const width = 340;
  const height = 150;
  const padL = 44;
  const padR = 12;
  const padT = 12;
  const padB = 24;
  const coral = theme.palette.secondary.main;

  // The API's lower/upper band (a point's neighbors, not a statistical CI —
  // see ProjectionPoint) is only drawn when every point actually carries it.
  const hasBand = projection.every((p) => p.upper !== undefined && p.lower !== undefined);

  const dataMax = Math.max(...projection.map((p) => Math.max(p.amount, p.upper ?? p.amount)), 1);
  const yTicks = niceTicks(dataMax);
  const yStep = yTicks[1] - yTicks[0];
  const maxAmount = yTicks[yTicks.length - 1];
  const ages = projection.map((p) => p.age);
  const minAge = Math.min(...ages);
  const maxAge = Math.max(...ages);
  const ageSpan = maxAge - minAge || 1;

  const x = (age: number) => padL + ((age - minAge) / ageSpan) * (width - padL - padR);
  const y = (amount: number) =>
    padT + (1 - amount / maxAmount) * (height - padT - padB);

  const pts = projection.map((p) => ({ x: x(p.age), y: y(p.amount) }));
  const linePath = smoothPath(pts);
  const bandPath = hasBand
    ? smoothBandPath(
        projection.map((p) => ({ x: x(p.age), y: y(p.lower ?? p.amount) })),
        projection.map((p) => ({ x: x(p.age), y: y(p.upper ?? p.amount) })),
      )
    : `${linePath} L ${x(maxAge).toFixed(1)} ${(height - padB).toFixed(1)}` +
      ` L ${x(minAge).toFixed(1)} ${(height - padB).toFixed(1)} Z`;

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${width} ${height}`}
      sx={{ width: '100%', height: 'auto', display: 'block' }}
      role="img"
      aria-label="Projected wealth savings by child age"
    >
      {yTicks.map((tick) => (
        <g key={tick}>
          <line
            x1={padL}
            x2={width - padR}
            y1={y(tick)}
            y2={y(tick)}
            stroke={theme.palette.divider}
            strokeWidth={0.75}
          />
          <text
            x={padL - 6}
            y={y(tick) + 3}
            textAnchor="end"
            fontFamily={theme.typography.fontFamily}
            fontWeight={400}
            fontSize={10}
            letterSpacing={0}
            fill={theme.palette.text.secondary}
          >
            {fmtTick(tick, yStep)}
          </text>
        </g>
      ))}

      {/* The y-axis line itself — the horizontal gridlines above only mark
          ticks, they don't imply a visible axis on their own. */}
      <line
        x1={padL}
        x2={padL}
        y1={padT}
        y2={height - padB}
        stroke={theme.palette.text.secondary}
        strokeWidth={1}
      />

      <path d={bandPath} fill={coral} fillOpacity={hasBand ? 0.14 : 0.1} />
      <path d={linePath} fill="none" stroke={coral} strokeWidth={2} />

      {projection.map((p) => (
        <circle key={p.age} cx={x(p.age)} cy={y(p.amount)} r={3} fill={coral} />
      ))}

      {/* Every other age point, not every one — at 10px the full set of
          labels crowds/overlaps where intervals are short (e.g. 2.5 → 4). */}
      {projection
        .filter((_, i) => i % 2 === 0)
        .map((p) => (
        <text
          key={`x-${p.age}`}
          x={x(p.age)}
          y={height - 8}
          textAnchor="middle"
          fontFamily={theme.typography.fontFamily}
          fontWeight={400}
          fontSize={10}
          letterSpacing={0}
          fill={theme.palette.text.secondary}
        >
          {p.age === minAge ? `Age ${p.age}` : p.age}
        </text>
      ))}
    </Box>
  );
}

export function ProjectionsCard({ result, resultError, amountDollars }: ProjectionsCardProps) {
  const hasSelection = !!result && result.reachChildren > 0;

  return (
    <Box sx={styles.root}>
      <Typography component="h2" sx={styles.title}>
        Impact projections
      </Typography>

      {!result ? (
        <Typography sx={resultError ? styles.errorText : styles.headline}>
          {resultError ?? 'Loading impact data…'}
        </Typography>
      ) : hasSelection ? (
        <>
          <Typography sx={styles.headline}>
            Your{' '}
            <Box component="span" sx={styles.hlPrimary}>
              {formatCompact(amountDollars, true)}
            </Box>{' '}
            reaches{' '}
            <Box component="span" sx={styles.hlCoral}>
              {formatCompact(result.reachChildren)} children
            </Box>{' '}
            across your selected areas, creating an estimated{' '}
            <Box component="span" sx={styles.hlCoral}>
              {formatCompact(result.totalWealthSavings, true)}
            </Box>{' '}
            in total wealth savings.
          </Typography>

          <ProjectionChart result={result} />

          <Typography sx={styles.footnote}>
            Each child receives <InfoTooltip tooltip={PER_CHILD_TOOLTIP} compact />:{' '}
            <Box component="span" sx={styles.hlPrimary}>
              $
              {result.perChild >= 100
                ? Math.round(result.perChild).toLocaleString()
                : result.perChild.toFixed(2)}
            </Box>
          </Typography>
        </>
      ) : (
        <Typography sx={styles.emptyHeadline}>
          Select one or more areas on the map to project how your gift reaches
          children and grows over time.
        </Typography>
      )}
    </Box>
  );
}
